import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Card, CardContent, Box, Typography, Button, CircularProgress,
  Table, TableHead, TableRow, TableCell, TableBody, Divider, TextField, IconButton,
  FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel, Autocomplete,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { toast } from 'react-toastify';
import Invoice from '../../models/invoice';
import Packages from '../../collections/packages';
import Products from '../../collections/products';
import InvoiceStatusChip from './InvoiceStatusChip';
import HddtAdjustDialog from './HddtAdjustDialog';
import HddtCancelDialog from './HddtCancelDialog';

const formatVND = (n) => `${(n || 0).toLocaleString('vi-VN')} đ`;
const LINE_TYPE_LABELS = {
  PACKAGE_FEE: 'Gói tháng', OVERAGE: 'Vượt gói', INSTANT_ITEM: 'Mua lẻ',
  EXTENSION: 'Gia hạn', SHIPPING: 'Vận chuyển', PRODUCT: 'Sản phẩm',
};
const LINE_TYPE_OPTIONS = ['PRODUCT', 'INSTANT_ITEM', 'PACKAGE_FEE', 'EXTENSION', 'OVERAGE', 'SHIPPING'];
// S22: these line types name a package, so their description comes from the catalog.
const isPackageLine = (l) => ['PACKAGE_FEE', 'EXTENSION'].includes(l.line_type);
// S23: a product line searches the product list, matching the builder. An untyped
// line counts as one — that is what the type dropdown shows for it.
const isProductLine = (l) => (l.line_type || 'PRODUCT') === 'PRODUCT';
const sourceLabel = (inv) => {
  if (inv.invoice_type === 'ORDER') return `Đơn hàng #${inv.order_id}`;
  if (inv.invoice_type === 'USER_PACKAGE') return `Gói dịch vụ #${inv.user_package_id}`;
  return 'Tùy chỉnh';
};

const InvoiceDetail = ({ setPageTitle }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // S15: DRAFT invoices are editable in place (lines + note).
  const [editing, setEditing] = useState(false);
  const [draftLines, setDraftLines] = useState([]);
  const [draftNote, setDraftNote] = useState('');
  const [discountType, setDiscountType] = useState('ALL');
  const [discountPercent, setDiscountPercent] = useState('');
  const [packageCatalog, setPackageCatalog] = useState([]);
  const [products, setProducts] = useState([]);

  const load = async () => {
    try {
      const data = await new Invoice().getById(id);
      setInvoice(data);
    } catch (e) {
      toast.error('Không thể tải hóa đơn');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (setPageTitle) setPageTitle('Chi tiết hóa đơn'); load(); /* eslint-disable-line */ }, [id]);

  const refresh = async () => { setLoading(true); await load(); };

  // --- S15 edit mode -------------------------------------------------------
  const startEdit = () => {
    setDraftLines((invoice.line_items || []).map((l) => ({
      description: l.description || '',
      line_type: l.line_type || null,
      quantity: l.quantity ?? 0,
      unit: l.unit || 'kg',
      unit_price: l.unit_price ?? 0,
      product_id: l.product_id ?? null,
      order_line_item_id: l.order_line_item_id ?? null,
    })));
    setDraftNote(invoice.note || '');
    setDiscountType('ALL');
    setDiscountPercent('');
    setEditing(true);
    loadPickerOptions();
  };

  // Only the edit form needs these, and only once per visit. Either failing just
  // leaves that description as free text.
  const loadPickerOptions = async () => {
    if (!packageCatalog.length) {
      try {
        const list = new Packages();
        await list.fetch({ per_page: 100, status: 'ACTIVE' });
        setPackageCatalog(list.toArray());
      } catch (e) { /* catalog optional */ }
    }
    if (!products.length) {
      try {
        const list = new Products();
        await list.fetch('ACTIVE');
        setProducts(list.toArray());
      } catch (e) { /* products optional */ }
    }
  };

  const cancelEdit = () => { setEditing(false); setDraftLines([]); setDraftNote(''); };

  const setLine = (i, field, value) => setDraftLines((prev) =>
    prev.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)));

  const addLine = () => setDraftLines((prev) => [...prev,
    { description: '', line_type: 'PRODUCT', quantity: 1, unit: 'kg', unit_price: 0 }]);

  const addPackageLine = () => setDraftLines((prev) => [...prev,
    { description: '', line_type: 'PACKAGE_FEE', quantity: 1, unit: 'gói', unit_price: 0 }]);

  const removeLine = (i) => setDraftLines((prev) => prev.filter((_, idx) => idx !== i));

  // S23: picking a product carries its price and id, so the line matches the catalog.
  const selectProduct = (i, product) => {
    if (!product || typeof product !== 'object') return;
    setDraftLines((prev) => prev.map((l, idx) => (idx === i
      ? { ...l, description: product.name, unit_price: product.latest_price || 0,
        product_id: product.id }
      : l)));
  };

  // S22: fill a package line the way the server writes one — name in quotes, one "gói".
  const selectPackage = (i, pkg) => {
    if (!pkg || typeof pkg !== 'object') return;
    setDraftLines((prev) => prev.map((l, idx) => (idx === i
      ? {
        ...l,
        description: `${l.line_type === 'EXTENSION' ? 'Gia hạn gói' : 'Gói tháng'} "${pkg.name}"`,
        quantity: 1, unit: 'gói', unit_price: pkg.price || 0,
      }
      : l)));
  };

  // S21: renewal as an explicit opt-in here too. The edit form replaces lines
  // wholesale, so the switch adds/removes the EXTENSION line rather than
  // re-running a builder flag. Off unless the invoice already carries one.
  const hasExtensionLine = draftLines.some((l) => l.line_type === 'EXTENSION');
  const toggleExtension = (on) => {
    if (!on) {
      setDraftLines((prev) => prev.filter((l) => l.line_type !== 'EXTENSION'));
      return;
    }
    if (hasExtensionLine) return;
    // Autofill the price: the package's own price (sent by the API, so it works even
    // when this invoice has no package-fee line), else the fee line on the invoice.
    const fee = draftLines.find((l) => l.line_type === 'PACKAGE_FEE');
    const price = Number(invoice.renewal?.price) || Number(fee?.unit_price) || 0;
    const name = invoice.renewal?.package_name
      || (fee ? fee.description.replace(/^Gói tháng /, '').replace(/"/g, '') : null);
    setDraftLines((prev) => [...prev, {
      description: name ? `Gia hạn gói "${name}"` : 'Gia hạn gói',
      line_type: 'EXTENSION',
      quantity: 1,
      unit: 'gói',
      unit_price: price,
    }]);
  };

  // Discount: reduce the unit price of every line of a chosen type (or all),
  // the same rule the invoice builder applies.
  const applyDiscount = () => {
    const pct = Number(discountPercent);
    if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
      toast.warn('Nhập phần trăm chiết khấu từ 1 đến 100');
      return;
    }
    const matches = (l) => discountType === 'ALL' || (l.line_type || 'PRODUCT') === discountType;
    const hit = draftLines.filter(matches).length;
    if (!hit) {
      toast.warn('Không có dòng nào thuộc loại đã chọn');
      return;
    }
    setDraftLines((prev) => prev.map((l) => (matches(l)
      ? { ...l, unit_price: Math.round((Number(l.unit_price) || 0) * (1 - pct / 100)) }
      : l)));
    toast.success(`Đã giảm ${pct}% cho ${hit} dòng`);
  };

  // Mirrors the server rule: round(quantity x unit_price) per line.
  const draftTotal = draftLines.reduce(
    (sum, l) => sum + Math.round((Number(l.quantity) || 0) * (Number(l.unit_price) || 0)), 0);

  const saveEdit = () => act(async () => {
    if (!draftLines.length) throw new Error('Hóa đơn phải có ít nhất một dòng hàng');
    if (draftLines.some((l) => !l.description.trim())) {
      throw new Error('Mỗi dòng hàng cần có mô tả');
    }
    await new Invoice().update(invoice.id, {
      note: draftNote,
      line_items: draftLines.map((l) => ({
        description: l.description.trim(),
        line_type: l.line_type || undefined,
        quantity: Number(l.quantity) || 0,
        unit: l.unit || 'kg',
        unit_price: Number(l.unit_price) || 0,
        product_id: l.product_id ?? undefined,
        order_line_item_id: l.order_line_item_id ?? undefined,
      })),
    });
    setEditing(false);
  }, 'Đã lưu hóa đơn');

  const act = async (fn, successMsg) => {
    setBusy(true);
    try { await fn(); toast.success(successMsg); await refresh(); }
    catch (e) {
      // eslint-disable-next-line no-console
      console.error('invoice action failed:', e);
      const msg = e?.response?.data?.error || e?.message || 'Thao tác thất bại';
      toast.error(typeof msg === 'string' ? msg : 'Thao tác thất bại');
    }
    finally { setBusy(false); }
  };

  // The backend streams the PDF as a binary file; save it via an anchor (popup-blocker-proof).
  const downloadPdf = async (g) => {
    const blob = await g.download();
    if (!blob) { toast.error('Không tải được PDF'); return; }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `HDDT-${g.hddt_sh_don || g.id}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // Internal invoice as a receipt IMAGE with a VietQR payment QR (send to customer).
  const downloadInvoicePdf = async () => {
    const blob = await new Invoice().getPdf(invoice.id);
    if (!blob) { toast.error('Không tạo được hình hóa đơn'); return; }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${invoice.code}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  if (loading) return <Container sx={{ mt: 3 }}><CircularProgress /></Container>;
  if (!invoice) return (
    <Container sx={{ mt: 3 }}>
      <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mb: 2 }}>Quay lại</Button>
      <Typography>Không tìm thấy hóa đơn.</Typography>
    </Container>
  );

  const gov = invoice.government_invoice;
  const lines = invoice.line_items || [];
  const history = invoice.activities || [];
  const hddtStatus = gov?.hddt_status;
  // Editable until the money is in or a tax document exists.
  const editable = ['DRAFT', 'SENT'].includes(invoice.status);
  // A package invoice: linked by the FK, by the compose junction, or by its type.
  const hasPackageSource = Boolean(
    invoice.user_package_id
    || invoice.invoice_type === 'USER_PACKAGE'
    || (invoice.sources || []).some((s) => s.source_type === 'USER_PACKAGE'),
  );

  return (
    <Container sx={{ mt: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Button variant="outlined" onClick={() => navigate(-1)}>Quay lại</Button>
      </Box>

      {/* HÓA ĐƠN NỘI BỘ */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
            <Typography variant="h6">Hóa đơn {invoice.code}</Typography>
            <InvoiceStatusChip status={invoice.status} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            {/* S15: correctable while unpaid and not yet an HDDT (DRAFT or SENT). */}
            {editable && (editing ? (
              <>
                <Button variant="contained" size="small" disabled={busy} onClick={saveEdit}>
                  Lưu
                </Button>
                <Button variant="outlined" size="small" disabled={busy} onClick={cancelEdit}>
                  Hủy chỉnh sửa
                </Button>
              </>
            ) : (
              <Button variant="outlined" size="small" startIcon={<EditIcon />} disabled={busy}
                onClick={startEdit}>Chỉnh sửa</Button>
            ))}
            {invoice.status === 'DRAFT' && !editing && (
              <Button variant="contained" size="small" disabled={busy}
                onClick={() => act(async () => {
                  await new Invoice().updateStatus(invoice.id, 'SENT');
                }, 'Đã gửi hóa đơn')}>Gửi hóa đơn</Button>
            )}
            {invoice.status === 'SENT' && (
              <Button variant="contained" color="success" size="small" disabled={busy}
                onClick={() => act(async () => {
                  await new Invoice().updateStatus(invoice.id, 'PAID');
                }, 'Đã đánh dấu thanh toán')}>Đánh dấu đã thanh toán</Button>
            )}
            {(invoice.status === 'SENT' || invoice.status === 'DRAFT') && (
              <Button variant="outlined" size="small" disabled={busy}
                onClick={() => navigate(`/invoices/${invoice.id}/bank-match`)}>Khớp ngân hàng</Button>
            )}
            <Button variant="outlined" size="small" startIcon={<DownloadIcon />} disabled={busy}
              onClick={downloadInvoicePdf}>Tải hóa đơn (QR)</Button>
            {editable && invoice.order_id && (
              <Button variant="outlined" size="small" color="warning" disabled={busy}
                onClick={() => act(async () => {
                  await new Invoice().syncFromOrder(invoice.id);
                }, 'Đã đồng bộ hóa đơn từ đơn hàng')}>Đồng bộ từ đơn hàng</Button>
            )}
            {/* Package invoices: re-pull the package's lines after it changed. */}
            {editable && hasPackageSource && (
              <Button variant="outlined" size="small" color="warning" disabled={busy}
                onClick={() => act(async () => {
                  await new Invoice().syncFromUserPackage(invoice.id);
                }, 'Đã đồng bộ hóa đơn từ gói dịch vụ')}>Đồng bộ từ gói dịch vụ</Button>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Nguồn: {sourceLabel(invoice)} · Khách hàng: {invoice.user?.name || `#${invoice.user_id}`}
            {invoice.paid_date ? ` · Thanh toán: ${new Date(invoice.paid_date).toLocaleDateString('vi-VN')}` : ''}
          </Typography>
          {editing && (
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap',
                       mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600,
                textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Chiết khấu
              </Typography>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="edit-discount-type-label">Áp dụng cho</InputLabel>
                <Select labelId="edit-discount-type-label" label="Áp dụng cho"
                  value={discountType} onChange={(e) => setDiscountType(e.target.value)}>
                  <MenuItem value="ALL">Tất cả các dòng</MenuItem>
                  {LINE_TYPE_OPTIONS.map((t) => (
                    <MenuItem key={t} value={t}>{LINE_TYPE_LABELS[t]}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField size="small" type="number" label="Phần trăm (%)" sx={{ width: 130 }}
                value={discountPercent} inputProps={{ min: 1, max: 100 }}
                onChange={(e) => setDiscountPercent(e.target.value)} />
              <Button variant="outlined" onClick={applyDiscount}>Áp dụng</Button>
              <Box sx={{ flexGrow: 1 }} />
              <FormControlLabel
                control={(
                  <Switch checked={hasExtensionLine}
                    onChange={(e) => toggleExtension(e.target.checked)} />
                )}
                label="Gia hạn gói"
              />
            </Box>
          )}
          <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Loại</TableCell><TableCell sx={{ minWidth: 260 }}>Mô tả</TableCell>
              <TableCell align="right">SL</TableCell><TableCell align="right">Đơn giá</TableCell>
              <TableCell align="right">Thành tiền</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {editing ? (
                <>
                  {draftLines.map((l, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Select size="small" sx={{ minWidth: 120 }}
                          value={l.line_type || 'PRODUCT'}
                          inputProps={{ 'aria-label': `Loại dòng ${i + 1}` }}
                          onChange={(e) => setLine(i, 'line_type', e.target.value)}>
                          {LINE_TYPE_OPTIONS.map((t) => (
                            <MenuItem key={t} value={t}>{LINE_TYPE_LABELS[t]}</MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell>
                        {isPackageLine(l) ? (
                          <Autocomplete
                            freeSolo
                            options={packageCatalog}
                            getOptionLabel={(o) => (typeof o === 'string' ? o : o.name || '')}
                            slotProps={{ popper: { style: { width: 'fit-content', minWidth: 300 } } }}
                            renderOption={(props, o) => (
                              <li {...props} key={o.id} style={{ whiteSpace: 'nowrap' }}>
                                {`${o.name} — ${formatVND(o.price)}`}
                              </li>
                            )}
                            inputValue={l.description || ''}
                            onInputChange={(e, v, reason) => {
                              if (reason === 'input' || reason === 'clear') setLine(i, 'description', v);
                            }}
                            onChange={(e, v) => selectPackage(i, v)}
                            renderInput={(params) => (
                              <TextField {...params} size="small" placeholder="Chọn gói dịch vụ..."
                                inputProps={{ ...params.inputProps, 'aria-label': `Mô tả dòng ${i + 1}` }} />
                            )}
                          />
                        ) : isProductLine(l) ? (
                          <Autocomplete
                            freeSolo
                            options={products}
                            getOptionLabel={(o) => (typeof o === 'string' ? o : o.name || '')}
                            inputValue={l.description || ''}
                            onInputChange={(e, v, reason) => {
                              if (reason === 'input' || reason === 'clear') setLine(i, 'description', v);
                            }}
                            onChange={(e, v) => selectProduct(i, v)}
                            renderInput={(params) => (
                              <TextField {...params} size="small" placeholder="Tìm sản phẩm..."
                                inputProps={{ ...params.inputProps, 'aria-label': `Mô tả dòng ${i + 1}` }} />
                            )}
                          />
                        ) : (
                          <TextField size="small" fullWidth value={l.description}
                            inputProps={{ 'aria-label': `Mô tả dòng ${i + 1}` }}
                            onChange={(e) => setLine(i, 'description', e.target.value)} />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <TextField size="small" type="number" sx={{ width: 90 }} value={l.quantity}
                          inputProps={{ 'aria-label': `Số lượng dòng ${i + 1}` }}
                          onChange={(e) => setLine(i, 'quantity', e.target.value)} />
                      </TableCell>
                      <TableCell align="right">
                        <TextField size="small" type="number" sx={{ width: 130 }} value={l.unit_price}
                          inputProps={{ 'aria-label': `Đơn giá dòng ${i + 1}` }}
                          onChange={(e) => setLine(i, 'unit_price', e.target.value)} />
                      </TableCell>
                      <TableCell align="right">
                        {formatVND(Math.round((Number(l.quantity) || 0) * (Number(l.unit_price) || 0)))}
                      </TableCell>
                      <TableCell align="right" padding="none">
                        <IconButton size="small" aria-label={`Xóa dòng ${i + 1}`}
                          onClick={() => removeLine(i)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Button size="small" startIcon={<AddIcon />} onClick={addLine}>
                        Thêm dòng
                      </Button>
                      <Button size="small" startIcon={<AddIcon />} onClick={addPackageLine}>
                        Thêm gói dịch vụ
                      </Button>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={4} align="right"><strong>Tổng cộng</strong></TableCell>
                    <TableCell align="right"><strong>{formatVND(draftTotal)}</strong></TableCell>
                    <TableCell padding="none" />
                  </TableRow>
                </>
              ) : (
                <>
                  {lines.map((l, i) => (
                    <TableRow key={i}>
                      <TableCell>{LINE_TYPE_LABELS[l.line_type] || l.line_type}</TableCell>
                      <TableCell>{l.description}</TableCell>
                      <TableCell align="right">{l.quantity}</TableCell>
                      <TableCell align="right">{formatVND(l.unit_price)}</TableCell>
                      <TableCell align="right">{formatVND(l.total_price)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={4} align="right"><strong>Tổng cộng</strong></TableCell>
                    <TableCell align="right"><strong>{formatVND(invoice.total)}</strong></TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
          </Box>
          {editing && (
            <TextField label="Ghi chú" size="small" fullWidth multiline minRows={2} sx={{ mt: 2 }}
              value={draftNote} onChange={(e) => setDraftNote(e.target.value)} />
          )}
        </CardContent>
      </Card>

      {/* HÓA ĐƠN ĐIỆN TỬ (HDDT) */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>Hóa đơn điện tử (HDDT)</Typography>
          {!gov ? (
            /* S13: the HDDT documents the delivery and lives on the ORDER, not here.
               This panel only shows HDDTs linked the pre-S13 way (legacy rows). */
            <Typography color="text.secondary">
              Chưa có HDDT. Hóa đơn điện tử được tạo trên trang đơn hàng (theo lần giao hàng).
            </Typography>
          ) : (
            <>
              <Box sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <InvoiceStatusChip variant="hddt" status={hddtStatus} />
                {gov.hddt_sh_don && <Typography variant="body2">Số HĐ: {gov.hddt_sh_don}</Typography>}
                <Typography variant="body2">Mẫu: {gov.hddt_khmsh_don}/{gov.hddt_khh_don}</Typography>
                {gov.related_gov_invoice_id && (
                  <Typography variant="body2">Liên quan HĐ #{gov.related_gov_invoice_id}</Typography>
                )}
              </Box>
              {gov.cancel_reason && <Typography variant="body2" color="error">Lý do hủy: {gov.cancel_reason}</Typography>}
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {hddtStatus === 'draft' && (
                  <Button variant="contained" disabled={busy}
                    onClick={() => act(async () => { await gov.sign(); }, 'Đã ký & phát hành HDDT')}>
                    Ký & phát hành
                  </Button>
                )}
                {hddtStatus === 'signed' && (
                  <>
                    <Button variant="outlined" startIcon={<DownloadIcon />} disabled={busy}
                      onClick={() => act(async () => { await downloadPdf(gov); }, 'Đã tải PDF')}>Tải PDF</Button>
                    <Button variant="outlined" disabled={busy} onClick={() => setAdjustOpen(true)}>Điều chỉnh</Button>
                    <Button variant="outlined" disabled={busy}
                      onClick={() => act(async () => {
                        await gov.replace({ line_items: invoice.line_items,
                          buyer_info: { name: gov.hddt_buyer_name, tax_code: gov.hddt_buyer_tax_code } });
                      }, 'Đã tạo HĐ thay thế (nháp)')}>Thay thế</Button>
                    <Button variant="outlined" color="error" disabled={busy}
                      onClick={() => setCancelOpen(true)}>Hủy HĐ</Button>
                  </>
                )}
                {(hddtStatus === 'cancelled' || hddtStatus === 'replaced' || hddtStatus === 'adjusted') && (
                  <Button variant="outlined" startIcon={<DownloadIcon />} disabled={busy}
                    onClick={() => act(async () => { await downloadPdf(gov); }, 'Đã tải PDF')}>Tải PDF</Button>
                )}
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* LỊCH SỬ */}
      {!!history.length && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>Lịch sử</Typography>
            {history.map((h, i) => (
              <Box key={i} sx={{ py: 0.5 }}>
                <Typography variant="body2">
                  {h.created_at ? new Date(h.created_at).toLocaleString('vi-VN') : ''} · {h.message || h.action}
                  {h.actor_name || h.by ? ` (bởi ${h.actor_name || h.by})` : ''}
                </Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}

      {gov && (
        <>
          <HddtAdjustDialog open={adjustOpen} governmentInvoice={gov}
            onClose={() => setAdjustOpen(false)} onDone={refresh} />
          <HddtCancelDialog open={cancelOpen} governmentInvoice={gov}
            onClose={() => setCancelOpen(false)} onDone={refresh} />
        </>
      )}
    </Container>
  );
};

export default InvoiceDetail;
